<template>
  <div class="taggregator">
    <div class="info-main-row">
      <label for="taggregator-input" class="taggregator-label">
        {{label}}
        <b-tooltip v-if="!!explainDisabled" :target="`taggregator-input-field-${name}`" placement="top" :title="explainDisabled"></b-tooltip>
        <div :id="`taggregator-input-field-${name}`">
          <input :id="inputId" type="text" name="taggregator-input" class="form-control" @input="onChange" v-model="search" @focus="filterSearchResults"
            @blur="handleClickOutside" @keydown.down="onArrowDown" @keydown.up="onArrowUp" @keydown.enter="onEnter"
            :disabled="isDisabled" :placeholder="placeholder" />
        </div>
      </label>
    </div>
    <ul id="taggregator-search-results" v-show="isSearching" class="taggregator-search-results">
      <li class="loading" v-if="isLoading">
        Loading results...
      </li>
      <li class="taggregator-search-result create-new-selection" v-if="createItemCallback" @mousedown="newSelection()">
        <b>Add New +</b>
      </li>
      <li class="taggregator-search-result quick-add-new-selection unselectable" v-if="quickAddCallback && !quickAddValid">
        <div class="quick-add-container">
          <b-tooltip v-if="!quickAddValid" :target="'quick-add-blurb'" placement="left" :title="quickAddDescription"></b-tooltip>
          <div id="quick-add-blurb">
            <b>Quick Add {{search}}</b>
          </div>
        </div>
      </li>
      <li class="taggregator-search-result quick-add-new-selection" v-if="quickAddCallback && quickAddValid" @mousedown="quickAddSelection()">
        <b>Quick Add {{search}}</b>
      </li>
      <li v-if="!isLoading" v-for="(result, i) in searchResults" :key="i" @mousedown="makeSelection(result)" class="taggregator-search-result"
        :class="{ 'is-active': i === arrowCounter }">
        <slot :result="result" name="result">
          {{ result[labelKey] }}
        </slot>
      </li>
    </ul>

    <ul id="taggregator-selected-results" v-bind:class="{hidden:(selections > 0)}" class="taggregator-selected-results">
      <li>
        <b-card v-for="selection in selections" :key="selection.id" :value="selection" v-if="selection && selection.id && selection[labelKey]"
          tag="article" class="selected-result-card text-center">
          <slot :selection="selection" name="selection">
            <p class="card-text" v-if="selection[labelKey]">{{selection[labelKey]}}</p>
          </slot>

           <a v-if="!disabled" @click="removeSelection(selection)" class="remove-selection fi-x" v-b-tooltip.hover.top="'Remove'"></a>

        </b-card>
      </li>
    </ul>
  </div>
</template>

<script>
  export default {
    name: "Taggregator",
    props: {
      // Useds to specify what's being taggregated in notifications.
      name: {
        type: String,
        required: true,
        default: "taggregation"
      },
      quickAddDescription: {
        type: String,
        required: false,
        default: `Quick add value invalid`
      },
      inputId: {
        type: String,
        required: false,
        default: ""
      },
      // The array of all available items eligible to be taggregated
      items: {
        type: Array,
        required: true,
        default: () => []
      },
      // The items selected and taggregated by the user
      selections: {
        type: Array,
        required: true,
        default: () => []
      },
      // Allows an async request to map to items
      isAsync: { // TODO: Doesn't work yet
        type: Boolean,
        required: false,
        default: false
      },
      // What's be displayed to the user as the label of the taggergated
      label: {
        type: String,
        require: false,
        default: ""
      },
      // Function to call whenever a user is "quick" added.
      quickAddCallback: {
        type: Function, // TODO - Accept RegEx
        require: false,
        default: null
      },
      // Number of words required for quick add
      quickAddMinWordCount: {
        type: Number,
        require: false,
        default: 2
      },
      // Number of words required for quick add
      quickAddMinWordLength: {
        type: Number,
        require: false,
        default: 2
      },
      // Whether or not a name must be unique to get quick added
      quickAddHasUniqueName: {
        type: Boolean,
        require: false,
        default: false
      },
      // Function to call whenever a user wants to add a new item to the items list
      // generally used to add that item to a data source
      createItemCallback: {
        type: Function,
        require: false,
        default: null
      },
      // The minimum number of selections allowed
      minimum: {
        type: Number,
        require: false,
        default: null
      },
      // The maximum number of selections allowed
      maximum: {
        type: Number,
        require: false,
        default: null
      },
      // Function to call whenever an item is selected
      onAdd: {
        type: Function,
        require: false,
        default: () => {}
      },
      // Function to call whenever an item is removed
      onRemove: {
        type: Function,
        require: false,
        default: () => {}
      },
      // The key in the object to use as the label
      labelKey: {
        type: String,
        require: false,
        default: "label"
      },
      // Self explanatory
      placeholder: {
        type: String,
        require: false,
        default: ""
      },

      disabled: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        isSearching: false,
        searchResults: [],
        search: "",
        isLoading: false,
        arrowCounter: 0,
        explainDisabled: false,
        quickAddValid: false
      };
    },
    computed: {
      isDisabled() {
        if (this.disabled) {
          return true;
        }

        this.explainDisabled = true;

        if (this.maximum && this.selections.length >= this.maximum) {
          this.explainDisabled = `You've already selected the maximum number of ${this.name}`;
          return true;
        }

        if (this.items.length < 1 && !this.createItemCallback && !this.quickAddCallback) {
          this.explainDisabled = `There are no available ${this.name} to select from`;
          return true;
        }

        this.explainDisabled = "";
        return false;
      }
    },
    methods: {
      onChange() {
        this.$emit("input", this);
        if (this.isAsync) {
          this.isLoading = true;
        } else {
          this.filterSearchResults();
          this.isSearching = true;
        }
      },
      clearSearchResults() {
        this.searchResults = [];
        this.isSearching = false;
      },
      filterSearchResults() {
        this.isSearching = true;
        this.searchResults = this.items
          .filter(item => this.selections.map(result => result.id).indexOf(item.id) === -1) // already selected items}
          .filter(item => item[this.labelKey].toLowerCase().indexOf(this.search.toLowerCase()) > -1); // string search
      },
      onArrowDown(evt) {
        if (this.arrowCounter < this.searchResults.length) {
          this.arrowCounter = this.arrowCounter + 1;
        }
      },
      onArrowUp() {
        if (this.arrowCounter > 0) {
          this.arrowCounter = this.arrowCounter - 1;
        }
      },
      onEnter() {
        const selection = this.searchResults[this.arrowCounter];
        if (selection) {
          this.makeSelection(selection);
          this.arrowCounter = -1;
        }
      },
      makeSelection(selection) {
        this.onAdd(selection, true);
        if (this.selections.map(selection => selection.id).indexOf(selection.id) === -1) {
          this.$emit("update:selections", this.selections.concat(selection));
        }

        this.search = "";
      },
      newSelection() {
        this.search = "";
        this.isSearching = false;
        this.createItemCallback();
      },
      quickAddSelection() {
        if (!this.quickAddValid) {
          this.filterSearchResults();
          return
        }
        this.quickAddCallback(this.search, this.selections);
        this.search = "";
        this.isSearching = false;
      },
      handleClickOutside(evt) {
        if (evt.currentTarget === document) return
        this.isSearching = false;
        this.arrowCounter = -1;
      },
      keymonitor: function (event) {
        if (this.disabledKeys.indexOf(event.key) >= 0) {
          event.preventDefault();
        }
      },
      removeSelection(selection) {
        if (this.selections.length === this.minimum) {
          this.onRemove(selection, false);
          return;
        }
        const selectedId = selection.id;

        this.onRemove(selection, true);
        this.$emit("update:selections", _.remove(this.selections, item => item.id !== selectedId));
      },
      isQuickAddValid() {
        const strArr = this.search.trim().split(" ");
        if (strArr.length >= this.quickAddMinWordCount && strArr.filter(str => str.length < this.quickAddMinWordLength)
          .length === 0) {
          let filterCount = this.items.filter(str => str[this.labelKey].localeCompare(this.search.trim())).length;
          if ((true === this.quickAddHasUniqueName) && (!(filterCount === this.items.length))) {
            this.quickAddValid = false;
          } else {
            this.quickAddValid = true;
          }
        } else {
          this.quickAddValid = false;
        }
      }
    },
    watch: {
      items: function (val, oldValue) {
        if (val.length !== oldValue.length) {
          this.searchResults = val;
          this.isLoading = false;
        }
      },
      search: function (val, oldValue) {
        if (this.quickAddCallback) this.isQuickAddValid();
      }
    },
    mounted() { // TODO - Find a more effecient way to mount this
      document.addEventListener("click", this.handleClickOutside);
    },
    destroyed() {
      document.removeEventListener("click", this.handleClickOutside);
    }
  };
</script>

<style lang="scss">
  .taggregator {
    position: relative;

    .taggregator-search-results {
      padding: 0;
      margin: -13px 0px;
      border: 1px solid #eeeeee;
      max-height: 225px;
      overflow: auto;
      width: 100%;
      position: absolute;
      z-index: 1;
      background: white;
    }

    .taggregator-search-result {
      list-style: none;
      text-align: left;
      padding: 4px 2px;
      cursor: pointer;
      background: white;
      padding: 5px;
      background: inherit;
    }

    .taggregator-search-result.is-active,
    .taggregator-search-result:hover {
      background-color: #4AAE9B;
      color: white;
    }

    .taggregator-label {
      width: 100%
    }

    .taggregator-selected-results {
      padding: 0;
    }

    .taggregator-selected-results>li {
      list-style: none;
      display: block;
      width: 100%;
    }

    .selected-result-card>.card-body {
      padding: 10px;
    }

    .selected-result-card .card-text {
      display: inline;
      /*text-align: center;*/
    }

    .selected-result-card {
      margin: 10px 0px 12px 0px;
      background: #FFFFFF;
      border: 1px solid #C2C2C2;
      /* This has gotta go eventually */
    }

    .selected-result-card:hover{
    box-shadow: -.5px .5px 1px .5px #C2C2C2;
    }

    a.remove-selection {
      cursor: pointer;
      font-weight: bold;
      display: inline;
      float: right;
      color: #333;
    }

    .unselectable {
      color: #aaaaaa;
      cursor: not-allowed;
    }

    .quick-add-container {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  .create-new-selection {
    font-weight: bold;
    text-align: center;
  }
</style>
